import { Injectable, signal } from '@angular/core';
import { fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { EmbedModeService } from '@core/runtime/embed-mode.service';
import {
  MicroappInitMessage,
  MicroappMessage,
  MicroappTokenMessage,
} from './microapp-message.types';

@Injectable({ providedIn: 'root' })
export class MicroappMessagingService {
  private parentOrigin = signal<string>('*');
  private accessToken = signal<string | null>(null);
  private config = signal<MicroappInitMessage | null>(null);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private _sessionExpired = signal<boolean>(false);
  private _refreshing = signal<boolean>(false);

  getAccessToken = this.accessToken.asReadonly();
  getConfig = this.config.asReadonly();
  sessionExpired = this._sessionExpired.asReadonly();
  refreshing = this._refreshing.asReadonly();

  constructor(private readonly embedMode: EmbedModeService) {
    const params = new URLSearchParams(window.location.search);

    if (this.embedMode.isPreview()) {
      this.loadPreviewConfig(params);
    }

    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter((event) => this.isValidMessage(event)),
        map((event) => event.data as MicroappMessage)
      )
      .subscribe((data) => this.handleMessage(data));
  }

  private loadPreviewConfig(params: URLSearchParams): void {
    const token = params.get('token');
    const tenant = params.get('tenant') ?? 'MX';
    const locale = params.get('locale') ?? 'es-CO';
    const theme = (params.get('theme') as 'light' | 'dark' | null) ?? 'light';

    if (token) {
      this.accessToken.set(token);
      localStorage.setItem('ma-token-dev', token);
      this.scheduleTokenRefresh(token);
    } else {
      const localToken = localStorage.getItem('ma-token-dev');
      if (localToken) {
        this.accessToken.set(localToken);
        this.scheduleTokenRefresh(localToken);
      }
    }

    this.config.set({
      type: 'ma:init',
      tenant,
      locale,
      theme,
      preferredAuth: 'bearer',
    });
  }

  private isValidMessage(event: MessageEvent): boolean {
    if (!event.data || typeof event.data !== 'object' || !event.data.type) {
      return false;
    }

    // TODO: validar event.origin contra allowlist configurable para produccion.
    return String(event.data.type).startsWith('ma:');
  }

  private handleMessage(data: MicroappMessage): void {
    switch (data.type) {
      case 'ma:init':
        this.config.set(data as MicroappInitMessage);
        break;
      case 'ma:token': {
        const tokenData = data as MicroappTokenMessage;
        this._sessionExpired.set(false);
        this._refreshing.set(false);
        this.accessToken.set(tokenData.accessToken);
        this.scheduleTokenRefresh(tokenData.accessToken);
        break;
      }
      default:
        break;
    }
  }

  private scheduleTokenRefresh(token: string): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      const exp = Number(payload.exp) * 1000;
      const delay = exp - Date.now() - 60_000;

      if (delay > 0) {
        this.refreshTimer = setTimeout(() => this.requestTokenRefresh(), delay);
      } else {
        this.requestTokenRefresh();
      }
    } catch {
      // token no parseable: no programar refresh automatico
    }
  }

  requestTokenRefresh(): void {
    if (this._refreshing()) return;

    this._sessionExpired.set(false);
    this._refreshing.set(true);

    if ((window as any).Android?.requestTokenRefresh) {
      (window as any).Android.requestTokenRefresh();
      return;
    }

    if ((window as any).webkit?.messageHandlers?.nativeBridge) {
      (window as any).webkit.messageHandlers.nativeBridge.postMessage({
        type: 'requestTokenRefresh',
      });
      return;
    }

    this.requestToken();

    if (!this.embedMode.isEmbedded()) {
      this._sessionExpired.set(true);
      this._refreshing.set(false);
    }
  }

  injectToken(token: string): void {
    this._sessionExpired.set(false);
    this._refreshing.set(false);
    this.accessToken.set(token);
    localStorage.setItem('ma-token-dev', token);

    const url = new URL(window.location.href);
    url.searchParams.set('token', token);
    window.history.replaceState({}, '', url.toString());

    this.scheduleTokenRefresh(token);
  }

  private sendToParent(message: MicroappMessage): void {
    if (!this.embedMode.isEmbedded()) return;
    window.parent.postMessage(message, this.parentOrigin());
  }

  notifyReady(): void {
    this.sendToParent({ type: 'ma:ready' });
  }

  notifyResize(height: number): void {
    this.sendToParent({ type: 'ma:resize', height });
  }

  requestToken(): void {
    this.sendToParent({ type: 'ma:request-token' });
  }

  emitEvent(name: string, payload: any): void {
    this.sendToParent({ type: 'ma:event', name, payload });
  }

  requestNavigation(path: string): void {
    this.sendToParent({ type: 'ma:navigate', path });
  }
}
